import React, { useState } from 'react'
import { api } from '../api'

export default function NewDossier({ onCreated, onCancel }) {
  const [step, setStep] = useState(1)
  const [siren, setSiren] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [entreprise, setEntreprise] = useState(null)
  const [form, setForm] = useState({
    effectif: '',
    medecin_travail: '',
    referent_securite: '',
    cse: null,
    date_mise_a_jour: new Date().toISOString().split('T')[0],
    facilitateur: '',
  })

  const lookupPappers = async () => {
    if (siren.replace(/\s/g, '').length !== 9) {
      setError('Le SIREN doit comporter 9 chiffres')
      return
    }
    setLoading(true)
    setError('')
    try {
      const cleanSiren = siren.replace(/\s/g, '')
      const res = await fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${cleanSiren}&limite=1`)
      const data = await res.json()
      if (data.results && data.results.length > 0) {
        const e = data.results[0]
        const siege = e.siege || {}
        setEntreprise({
          raison_sociale: e.nom_complet || e.nom_raison_sociale || '',
          forme_juridique: e.nature_juridique || '',
          siren: cleanSiren,
          siret: siege.siret || '',
          adresse: [siege.numero_voie, siege.type_voie, siege.libelle_voie, siege.code_postal, siege.libelle_commune]
            .filter(Boolean).join(' '),
          code_naf: siege.activite_principale || '',
          libelle_naf: siege.libelle_activite_principale || '',
          dirigeant: e.dirigeants && e.dirigeants[0]
            ? `${e.dirigeants[0].prenom || ''} ${e.dirigeants[0].nom || ''}`.trim()
            : '',
        })
        setStep(2)
      } else {
        setError('Entreprise non trouvée. Vérifiez le SIREN ou saisissez les informations manuellement.')
        setEntreprise({ siren: cleanSiren, raison_sociale: '', forme_juridique: '', siret: '', adresse: '', code_naf: '', libelle_naf: '', dirigeant: '' })
        setStep(2)
      }
    } catch {
      setError('Erreur de recherche. Vérifiez votre connexion.')
    } finally {
      setLoading(false)
    }
  }

  const handleEntrepriseChange = (field, value) => {
    setEntreprise(prev => ({ ...prev, [field]: value }))
  }

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleCreate = async () => {
    setLoading(true)
    setError('')
    try {
      const payload = {
        entreprise: {
          ...entreprise,
          effectif: parseInt(form.effectif) || null,
          medecin_travail: form.medecin_travail,
          referent_securite: form.referent_securite || null,
          cse: parseInt(form.effectif) >= 11 ? form.cse : null,
          date_mise_a_jour: form.date_mise_a_jour,
        },
        facilitateur: form.facilitateur || null,
      }
      const dossier = await api.createDossier(payload)
      onCreated(dossier)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const effectif = parseInt(form.effectif) || 0

  return (
    <div className="new-dossier">
      <div className="page-header">
        <button className="back-btn" onClick={onCancel}>← Retour</button>
        <h1 className="page-title">Nouveau dossier DUER</h1>
      </div>

      <div className="steps-indicator">
        <div className={`step-dot ${step >= 1 ? 'active' : ''}`}>1</div>
        <div className="step-line"></div>
        <div className={`step-dot ${step >= 2 ? 'active' : ''}`}>2</div>
        <div className="step-line"></div>
        <div className={`step-dot ${step >= 3 ? 'active' : ''}`}>3</div>
      </div>

      {step === 1 && (
        <div className="card">
          <h2 className="card-title">Étape 1 — Identification de l'entreprise</h2>
          <p className="card-subtitle">Entrez le SIREN pour récupérer automatiquement les informations.</p>
          <div className="form-group">
            <label className="form-label">Numéro SIREN (9 chiffres)</label>
            <input
              className="form-input form-input--large"
              value={siren}
              onChange={e => setSiren(e.target.value)}
              placeholder="ex : 978 826 907"
              maxLength={11}
              onKeyDown={e => e.key === 'Enter' && lookupPappers()}
              autoFocus
            />
          </div>
          {error && <p className="form-error">{error}</p>}
          <div className="form-actions">
            <button className="btn btn-primary" onClick={lookupPappers} disabled={loading}>
              {loading ? 'Recherche...' : 'Rechercher →'}
            </button>
          </div>
        </div>
      )}

      {step === 2 && entreprise && (
        <div className="card">
          <h2 className="card-title">Étape 2 — Vérification des informations</h2>
          <p className="card-subtitle">Informations récupérées automatiquement. Corrigez si nécessaire.</p>

          <div className="form-grid">
            {[
              { field: 'raison_sociale', label: 'Raison sociale' },
              { field: 'forme_juridique', label: 'Forme juridique' },
              { field: 'siret', label: 'SIRET' },
              { field: 'adresse', label: 'Adresse' },
              { field: 'code_naf', label: 'Code NAF' },
              { field: 'libelle_naf', label: 'Activité (NAF)' },
              { field: 'dirigeant', label: 'Dirigeant' },
            ].map(({ field, label }) => (
              <div key={field} className="form-group">
                <label className="form-label">{label}</label>
                <input
                  className="form-input"
                  value={entreprise[field] || ''}
                  onChange={e => handleEntrepriseChange(field, e.target.value)}
                />
              </div>
            ))}
          </div>

          {error && <p className="form-error">{error}</p>}
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setStep(1)}>← Retour</button>
            <button className="btn btn-primary" onClick={() => setStep(3)}>
              Confirmer →
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card">
          <h2 className="card-title">Étape 3 — Informations complémentaires</h2>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Effectif total *</label>
              <input
                className="form-input"
                type="number"
                min="1"
                value={form.effectif}
                onChange={e => handleFormChange('effectif', e.target.value)}
                placeholder="ex : 4"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Médecin du travail *</label>
              <input
                className="form-input"
                value={form.medecin_travail}
                onChange={e => handleFormChange('medecin_travail', e.target.value)}
                placeholder="ex : ACST Alsace"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Référent sécurité</label>
              <input
                className="form-input"
                value={form.referent_securite}
                onChange={e => handleFormChange('referent_securite', e.target.value)}
                placeholder="Nom ou Non désigné"
              />
            </div>
            {effectif >= 11 && (
              <div className="form-group">
                <label className="form-label">CSE (≥ 11 salariés)</label>
                <select
                  className="form-input"
                  value={form.cse === null ? '' : String(form.cse)}
                  onChange={e => handleFormChange('cse', e.target.value === 'true')}
                >
                  <option value="">Sélectionner</option>
                  <option value="true">Oui</option>
                  <option value="false">Non</option>
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Date de mise à jour du DUER</label>
              <input
                className="form-input"
                type="date"
                value={form.date_mise_a_jour}
                onChange={e => handleFormChange('date_mise_a_jour', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Facilitateur assigné</label>
              <input
                className="form-input"
                value={form.facilitateur}
                onChange={e => handleFormChange('facilitateur', e.target.value)}
                placeholder="Prénom Nom du facilitateur"
              />
            </div>
          </div>

          {error && <p className="form-error">{error}</p>}
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setStep(2)}>← Retour</button>
            <button className="btn btn-primary" onClick={handleCreate} disabled={loading}>
              {loading ? 'Création...' : 'Créer le dossier →'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
